export type ContactLink = {
  id: string;
  type: "telegram" | "whatsapp" | "phone" | "website" | "custom";
  placement: "storefront" | "seller";
  label: string;
  url: string;
  enabled: boolean;
  position: number;
};

export type StoreSettings = {
  name: string;
  description: string;
  avatarUrl?: string;
  contactLinks: ContactLink[];
};

export type ProductMedia = {
  id: string;
  url: string;
  position: number;
  altText?: string;
};

export type Product = {
  id: string;
  slug: string;
  title: string;
  description: string;
  price?: number;
  currency: string;
  availabilityStatus: "available" | "reserved" | "sold" | "hidden";
  visibility: "published" | "hidden";
  expiresAt: string;
  sellerContactUrl?: string;
  sellerContactLabel?: string;
  sellerContactMessage?: string;
  media: ProductMedia[];
};

const demoStore: StoreSettings = {
  name: "Telegram Shop",
  description: "Витрина товаров из Telegram-канала",
  avatarUrl: "/icons/icon.svg",
  contactLinks: [
    {
      id: "demo-contact",
      type: "telegram",
      placement: "storefront",
      label: "Подать объявление",
      url: "https://t.me/admin_example",
      enabled: true,
      position: 1
    },
    {
      id: "demo-seller-contact",
      type: "telegram",
      placement: "seller",
      label: "Написать продавцу",
      url: "https://t.me/seller_example",
      enabled: true,
      position: 1
    }
  ]
};

const demoProducts: Product[] = [
  {
    id: "demo-1",
    slug: "linen-shirt",
    title: "Льняная рубашка",
    description: "Свободная рубашка из плотного льна. Импортируйте реальные товары через Telegram webhook.",
    price: 49,
    currency: "EUR",
    availabilityStatus: "available",
    visibility: "published",
    expiresAt: new Date(Date.now() + 20 * 86400000).toISOString(),
    sellerContactUrl: "https://t.me/roomata",
    sellerContactLabel: "Написать @roomata",
    sellerContactMessage: "Здравствуйте! Интересует товар: Льняная рубашка\nЦена: 49,00 €",
    media: [
      {
        id: "demo-media-1",
        url: "https://images.unsplash.com/photo-1434389677669-e08b4cac3105?auto=format&fit=crop&w=900&q=80",
        position: 1,
        altText: "Льняная рубашка"
      },
      {
        id: "demo-media-1-detail",
        url: "https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?auto=format&fit=crop&w=900&q=80",
        position: 2,
        altText: "Льняная рубашка на модели"
      },
      {
        id: "demo-media-1-texture",
        url: "https://images.unsplash.com/photo-1503342217505-b0a15ec3261c?auto=format&fit=crop&w=900&q=80",
        position: 3,
        altText: "Деталь ткани льняной рубашки"
      }
    ]
  },
  {
    id: "demo-2",
    slug: "ceramic-cup",
    title: "Керамическая чашка",
    description: "Ручная работа, матовая глазурь, спокойная форма для ежедневного кофе.",
    price: 18,
    currency: "EUR",
    availabilityStatus: "available",
    visibility: "published",
    expiresAt: new Date(Date.now() + 12 * 86400000).toISOString(),
    media: [
      {
        id: "demo-media-2",
        url: "https://images.unsplash.com/photo-1514228742587-6b1558fcca3d?auto=format&fit=crop&w=900&q=80",
        position: 1,
        altText: "Керамическая чашка"
      }
    ]
  }
];

async function fetchJson<T>(url: string, init?: RequestInit, fallback?: T): Promise<T> {
  const response = await fetch(url, {
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    ...init
  });

  if (!response.ok) {
    if (fallback && isLocalDev()) return fallback;
    const details = await response.text().catch(() => "");
    throw new Error(`Request failed: ${response.status}${details ? ` ${details}` : ""}`);
  }

  return response.json() as Promise<T>;
}

export const api = {
  async getStore() {
    return fetchJson<StoreSettings>("/api/store", undefined, demoStore);
  },
  async getProducts() {
    return fetchJson<Product[]>("/api/products", undefined, demoProducts);
  },
  async getProduct(slug: string) {
    const fallback = demoProducts.find((product) => product.slug === slug) ?? demoProducts[0];
    return fetchJson<Product>(`/api/products/${slug}`, undefined, fallback);
  },
  async updateProduct(id: string, body: Partial<Product>) {
    return fetchJson<Product>(`/api/admin/products/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body)
    });
  },
  async updateContacts(contactLinks: ContactLink[]) {
    return fetchJson<StoreSettings>("/api/admin/store/contacts", {
      method: "PATCH",
      body: JSON.stringify({ contactLinks })
    });
  },
  async getAdminMe() {
    return fetchJson<{ ok: true }>("/api/admin/me");
  }
};

function isLocalDev() {
  return ["localhost", "127.0.0.1", ""].includes(window.location.hostname);
}
